<script setup lang="ts">
    import { computed, type Ref, ref } from 'vue';
    import {
        useCaseAdminStore,
    } from '@/views/admin/adminStore';
    import { logger } from '@/util';
    import type { ApiStudy } from '@/api/study/study';

    const props = defineProps<{ study: ApiStudy; }>();
    const log = logger();
    const store = useCaseAdminStore();

    const state: Ref<'hidden' | 'showing' | 'working' | 'error' | 'complete'> = ref('hidden');
    const _showCreateButton = computed(() => ['showing', 'working', 'error'].includes(state.value));

    const isVisible = computed({
        get: () => {
            return state.value !== 'hidden';
        },
        set: (value: boolean) => {
            if (value) {
                state.value = 'showing';
            } else {
                state.value = 'hidden';
            }
        },
    });

    const onCreate = async () => {
        try {
            state.value = 'working';
            await store.generateZippedDicomFile(props.study);
            state.value = 'complete';
        } catch (e) {
            log.error('Error while generating zipped dicoms: %s', e);
            state.value = 'error';
        }
    };
</script>

<template>
    <app-modal v-model="isVisible" width="600">
        <template #activator="{ props: activatorProps }">
            <v-btn
                height="30"
                color="primary"
                variant="outlined"
                density="compact"
                v-bind="activatorProps"
                rounded
                class="px-4"
            >
                <span>Generate Zipped Dicoms</span>
            </v-btn>
        </template>

        <v-card min-height="400px" class="d-flex-column fill-height">
            <app-modal-title class="headline" @close="isVisible = false">
                Generate Zipped Dicom File
            </app-modal-title>
            <app-modal-content v-if="state === 'complete'">
                <div class="d-flex ga-2 align-center">
                    <v-icon icon="mdi-check-circle" size="large" color="success" />
                    <p>Started generating zipped dicom file</p>
                </div>
            </app-modal-content>
            <app-modal-content v-else-if="state === 'error'">
                <div class="d-flex ga-2 align-center">
                    <v-icon icon="mdi-alert-circle" size="large" color="error" />
                    <p>Error while attempting to generate zipped dicom file - see logs</p>
                </div>
            </app-modal-content>
            <app-modal-content v-else>
                <span>
                    Generate a zip file containing all DICOM files associated with this study.
                </span>
            </app-modal-content>
            <v-spacer />
            <app-modal-content class="align-self-end">
                <app-button-primary
                    v-if="_showCreateButton"
                    :loading="state === 'working'"
                    @click="onCreate"
                >
                    Create
                </app-button-primary>
            </app-modal-content>
        </v-card>
    </app-modal>
</template>

<style scoped lang="scss">
    a {
        color: rgb(var(--v-theme-primary));
    }
</style>
